/**
 * Icon glyph catalogue used by `<push-button>` to render the engraved
 * symbols on broadcast keycaps. Each entry is an SVG path-d string drawn
 * inside a fixed `0 0 24 24` viewBox and rendered as a single inked
 * shape — the engraving on the real keycap is mono-colour, the LED
 * back-illumination bleeds around it, so we do not paint individual
 * fills.
 *
 * Adding an icon: keep the geometry inside the 24-unit box with a
 * one-unit safe margin on every side. Use round joins and round caps
 * implicitly (the consumer sets stroke-linecap/linejoin); set the
 * stroke-width via the consumer's CSS so the icon scales with the
 * button cap.
 *
 * Glyph names are kebab-case and chosen to match the broadcast control
 * vocabulary, not the typical web-iconography vocabulary. `light`,
 * `contrast`, `colour`, `enhance`, `automatics`, `home`, `diagnostics`,
 * `files`, `snapshot`, `settings`, `panel-lock`, `call`, `headset`,
 * `test-signal`, `recall-standard`, `waveform`, `free`, `prev`, `next`,
 * `up`, `down`, `left`, `right`. Spelling follows TCIS (British English).
 */
export const ICON_GLYPHS = {
  /** Sun: 4-point starburst + central disc — the CGA "Light" page. */
  light:
    'M12 4 L12 1 M12 23 L12 20 M4 12 L1 12 M23 12 L20 12 ' +
    'M6.34 6.34 L4.22 4.22 M19.78 19.78 L17.66 17.66 ' +
    'M6.34 17.66 L4.22 19.78 M19.78 4.22 L17.66 6.34 ' +
    'M12 7 A5 5 0 1 1 11.99 7 Z',

  /** Half-filled circle for contrast — the CGA "Contrast" page. */
  contrast:
    'M12 3 A9 9 0 1 0 12 21 A9 9 0 1 0 12 3 Z ' +
    'M12 3 L12 21 L12 21 A9 9 0 0 0 12 3 Z',

  /** Painter's palette with three colour wells — the CGA "Colour" page. */
  colour:
    'M12 3 C7 3 3 7 3 12 C3 16 6 18 9 18 L11 18 ' +
    'C11.5 18 12 18.5 12 19 C12 20 13 21 14 21 ' +
    'C19 21 21 17 21 13 C21 7 17 3 12 3 Z ' +
    'M8 9 A1.2 1.2 0 1 1 8 8.99 Z ' +
    'M16 8 A1.2 1.2 0 1 1 16 7.99 Z ' +
    'M17.5 13 A1.2 1.2 0 1 1 17.5 12.99 Z',

  /** Image frame with sparkle for the "Enhance" page (detail/sharpness). */
  enhance:
    'M3 5 L21 5 L21 19 L3 19 Z ' +
    'M3 16 L9 11 L13 14 L17 9 L21 14 ' +
    'M16 6 L17 8 L19 9 L17 10 L16 12 L15 10 L13 9 L15 8 Z',

  /** Circular arrow + 'A' for "Automatics" — auto-iris / auto-white. */
  automatics:
    'M12 4 A8 8 0 1 1 4.5 14.5 ' +
    'M4.5 14.5 L2.5 13.5 M4.5 14.5 L3.5 16.5 ' +
    'M9 16 L11.2 9.5 L12.8 9.5 L15 16 ' +
    'M9.7 14 L14.3 14',

  /** House outline — CGA "Home" page. */
  home:
    'M3 12 L12 4 L21 12 ' +
    'M5 11 L5 20 L19 20 L19 11 ' +
    'M10 20 L10 14 L14 14 L14 20',

  /** Waveform — Diagnostics. */
  diagnostics:
    'M2 12 L5 12 L7 4 L10 20 L13 8 L15 16 L17 12 L22 12',

  /** Folder outline — Files. */
  files:
    'M3 7 L10 7 L12 9 L21 9 L21 19 L3 19 Z ' +
    'M3 9 L21 9',

  /** Camera outline — Snapshot. */
  snapshot:
    'M5 8 L8 8 L9 6 L15 6 L16 8 L19 8 L19 18 L5 18 Z ' +
    'M12 10 A3.5 3.5 0 1 1 12 9.99 Z',

  /** Spanner / wrench — CGP Settings. */
  settings:
    'M14.5 2.5 L17.5 5.5 L15 8 L18 11 L21 8 L21 11 L18 14 ' +
    'L11 7 L8 10 L4 6 L6 4 Z',

  /** Padlock — Panel Lock. */
  'panel-lock':
    'M7 11 L7 9 A5 5 0 0 1 17 9 L17 11 ' +
    'M5 11 L19 11 L19 20 L5 20 Z ' +
    'M12 14 L12 17',

  /** Headset — Call. */
  call:
    'M4 14 L4 12 A8 8 0 0 1 20 12 L20 14 ' +
    'M3 14 L7 14 L7 20 L3 20 Z ' +
    'M17 14 L21 14 L21 20 L17 20 Z',

  /** Saw-tooth test-signal pattern. */
  'test-signal':
    'M2 18 L6 6 L6 18 L10 6 L10 18 L14 6 L14 18 L18 6 L18 18 L22 6',

  /** Folder with arrow — Recall Standard. */
  'recall-standard':
    'M3 7 L10 7 L12 9 L21 9 L21 19 L3 19 Z ' +
    'M3 9 L21 9 ' +
    'M9 14 L17 14 M14 11 L17 14 L14 17',

  /** Triangular play arrows — Prev/Next page tabs. */
  prev: 'M16 4 L8 12 L16 20 Z',
  next: 'M8 4 L16 12 L8 20 Z',

  /** Cursor arrows. */
  up: 'M12 6 L4 16 L20 16 Z',
  down: 'M12 18 L20 8 L4 8 Z',
  left: 'M8 12 L18 4 L18 20 Z',
  right: 'M16 12 L6 4 L6 20 Z',
} as const satisfies Record<string, string>;

export type PanelIconName = keyof typeof ICON_GLYPHS;

/**
 * Resolve an icon-name string to its path-d, returning `null` when the
 * name is not in the catalogue. Consumers can use this to fall back to
 * a text label without throwing.
 */
export function iconPath(name: string | undefined): string | null {
  if (!name) return null;
  return (ICON_GLYPHS as Record<string, string>)[name] ?? null;
}
